import { createMenuManagementAxiosInstance } from 'createAxiosInstance';
import { IMAGE_CONTENT_REQUEST_TYPE } from 'constants/cuboh';
import { logException } from 'helpers/loggers';

/* API helpers for the dashboard Photos tab (menu-management item-photos contract,
see api_contracts/item_photos_api.md in the menu-management repo). */

export type ItemPhotoVariant = {
  aspectRatio: string;
  imageUrl: string;
  resolution: string;
  webpUrl: string;
};

export type ItemPhotoRow = {
  uuid: string;
  name: string;
  enabled: boolean;
  imageUrl: string | null;
  photos: ItemPhotoVariant[];
};

type ItemPhotosPage = {
  count: number;
  next: string | null;
  previous?: string | null;
  results: ItemPhotoRow[];
};

const PAGE_LIMIT = 50;

/* Loads every item row for a menu, following the pagination envelope until `next`
is null. Guards against a misbehaving `next`: stops on an empty page, when `count`
rows have been collected, and advances the offset by the rows actually received
(the server may clamp `limit`). */
export async function fetchAllItemPhotos({
  companyId,
  merchantId,
  menuUuid,
}: {
  companyId: string | number;
  merchantId: string | number;
  menuUuid: string;
}): Promise<ItemPhotoRow[]> {
  const axios = await createMenuManagementAxiosInstance();
  const path = `/company/${companyId}/cuboh/merchant/${merchantId}/menu/${menuUuid}/item-photos`;
  const rows: ItemPhotoRow[] = [];
  const seenUuids = new Set<string>();
  let offset = 0;
  for (;;) {
    // Pages must be fetched sequentially: `count`/`next` can shift under concurrent edits.
    const { data }: { data: ItemPhotosPage } = await axios.get(path, {
      params: { limit: PAGE_LIMIT, offset },
    });
    const results = data?.results ?? [];
    const before = rows.length;
    results.forEach(row => {
      // Rows without a uuid can't be keyed or acted on — drop them.
      if (!row?.uuid) return;
      // Offset skew under concurrent edits can repeat a row across page boundaries.
      if (seenUuids.has(row.uuid)) return;
      seenUuids.add(row.uuid);
      rows.push(row);
    });
    if (!data?.next || !results.length) return rows;
    // Both early stops below fire while `next` is still set — surface the truncation
    // instead of failing silently.
    if (rows.length === before) {
      // No progress: all-duplicate page (proxy ignoring offset) or an all-uuid-less page.
      logException({
        message: 'item-photos pagination stopped with next still set',
        error: null,
        context: { menuUuid, reason: 'no-progress', collected: rows.length },
        tags: null,
      });
      return rows;
    }
    // Equalling `count` while `next` is set is a legal server state (e.g. `count` was
    // read before a concurrent insert) - only log if we've collected more than `count`,
    // since that can't happen without something actually being wrong.
    if (typeof data.count === 'number' && rows.length > data.count) {
      logException({
        message: 'item-photos pagination exceeded reported count with next still set',
        error: null,
        context: { menuUuid, reason: 'count-exceeded', count: data.count, collected: rows.length },
        tags: null,
      });
      return rows;
    }
    if (typeof data.count === 'number' && rows.length === data.count) {
      return rows;
    }
    offset += results.length;
  }
}

export type ItemImageFile = {
  companyId: string;
  createdAt: string;
  id: number;
  name: string;
  path: string;
  updatedAt: string;
  url: string;
};

type ItemImageParams = {
  companyId: string | number;
  merchantId: string | number;
  itemUuid: string;
};

/* Image-only item update: runs the BE image pipeline and returns the stored file plus
the refreshed item row (same shape as the list) for an in-place swap. */
export async function uploadItemImage({
  companyId,
  merchantId,
  itemUuid,
  file,
  imageEnhancedAt,
}: ItemImageParams & { file: File; imageEnhancedAt?: string }): Promise<{
  file: ItemImageFile;
  item: ItemPhotoRow;
}> {
  const axios = await createMenuManagementAxiosInstance(IMAGE_CONTENT_REQUEST_TYPE);
  const body = new FormData();
  body.append('file', file);
  // Omitted entirely when the saved image was not AI-enhanced, so the column stays null.
  if (imageEnhancedAt) body.append('image_enhanced_at', imageEnhancedAt);
  const response = await axios.post(
    `/company/${companyId}/cuboh/merchant/${merchantId}/item/${itemUuid}/image`,
    body,
  );
  return response?.data;
}

/* Clears the item's photo (imageUrl + all pre-cropped variants, DB-only) and returns
the refreshed item row. No-op 200 when the item has no photo. Throws when the response
lacks the item row so callers never feed undefined into state. */
export async function deleteItemImage({
  companyId,
  merchantId,
  itemUuid,
}: ItemImageParams): Promise<{ item: ItemPhotoRow }> {
  const axios = await createMenuManagementAxiosInstance();
  const response = await axios.delete(
    `/company/${companyId}/cuboh/merchant/${merchantId}/item/${itemUuid}/image`,
  );
  const data = response?.data;
  if (!data?.item) throw new Error('Delete succeeded but returned no item');
  return data;
}
